'use client';

import { FC, useLayoutEffect, useRef } from 'react';

import Image from '@/components/Image';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { MerchDto } from './types';

interface Props {
	data: MerchDto;
}

const MerchSection: FC<Props> = ({ data }) => {
	const device = useDevice();

	const wrapperRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		const titleLines = wrapper?.querySelectorAll(`.${css.title} p`);
		const mannequin = wrapper?.querySelector(`.${css.mannequin}`);

		if (!wrapper || !titleLines?.length || !mannequin) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: `(min-width: 992px)`,
					isMobile: `(max-width: 991px)`
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.set(titleLines, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleLines, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: titleLines,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					gsap.fromTo(
						mannequin,
						{
							yPercent: 10
						},
						{
							yPercent: -20,
							ease: 'none',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top bottom',
								end: 'bottom top',
								scrub: 1
							}
						}
					);
				}
			);
		}, wrapper);

		return () => {
			ctx.revert();
		};
	}, []);

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<Image
				className={css.blurBg}
				height={device === 'desktop' ? 760 : 180}
				width={device === 'desktop' ? 1680 : 390}
				src="/assets/images/about-us-page/merch/blur.webp"
				alt=""
			/>
			<img
				className={css.patternBg}
				src="/assets/images/about-us-page/merch/pattern.svg"
				alt=""
				fetchPriority="low"
			/>

			<div className={css.fade} />

			<div className={css.container}>
				<div className={css.content}>
					<Typography uppercase variant="h2" tag="h2" className={css.title}>
						<StrapiRichContent content={data.title} />
					</Typography>
					<div className={css.textWrapper}>
						<Typography uppercase variant="h5" tag="p" className={css.upperText}>
							{data.uppercaseText}
						</Typography>
						<Typography variant="body-m" tag="p">
							{data.text}
						</Typography>
					</div>
				</div>

				<Image
					className={css.mannequin}
					src="/assets/images/about-us-page/merch/mannequin.webp"
					alt="mannequin"
					width={800}
					height={1200}
					quality={100}
					preload={false}
				/>
			</div>
		</section>
	);
};

export default MerchSection;
